import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../../../../services/notification.service'; // NotificationService;
import { EmptyResultComponent } from '../../../../../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../../../../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { PackageMappingFormRow } from '../../../../../models/package-mapping.model';
import { PackageMappingService } from '../../../../../services/package-mapping.service';
import {
  arePackageMappingsDirty,
  getPackageMappingStatusClass,
  getPackageMappingStatusLabel,
  getPackageMappingWashTypeLabels,
  mapPackageMappingFromApi,
  validatePackageMappings,
} from '../../../../../utils/package-mapping.util';

@Component({
  selector: 'app-package-mappings',
  standalone: true,
  imports: [CommonModule, FormsModule, EmptyResultComponent, LoadingTableShimmerComponent],
  templateUrl: './package-mappings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./package-mappings.component.scss'],
})
export class PackageMappingsComponent implements OnChanges {
  @Input({ required: true }) companyId!: string;
  @Input({ required: true }) locationId!: string;
  @Input() wayConfigured = false;

  packageMappings: PackageMappingFormRow[] = [];
  loading = false;
  isSaving = false;
  loadFailed = false;
  listError = '';

  readonly shimmerColumns: ShimmerColumn[] = [
    { label: 'SaaS package', width: 'md' },
    { label: 'Wash type', width: 'sm' },
    { label: 'Way package ID', width: 'md' },
    { label: 'Status', width: 'sm' },
  ];

  private readonly packageMappingService = inject(PackageMappingService);
  private readonly messageService = inject(NotificationService);

  /** Bumped on context change / new save so stale PUT/GET handlers cannot unlock or overwrite. */
  private saveRequestId = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] || changes['locationId']) {
      this.loadPackageMappings();
    }
  }

  get isDirty(): boolean {
    return arePackageMappingsDirty(this.packageMappings);
  }

  getWashTypeLabels = getPackageMappingWashTypeLabels;
  getStatusLabel = getPackageMappingStatusLabel;
  getStatusClass = getPackageMappingStatusClass;

  onWayPackageIdChange(row: PackageMappingFormRow): void {
    row.fieldError = '';
    if (!this.loadFailed) {
      this.listError = '';
    }
  }

  onRetryLoad(): void {
    this.loadPackageMappings();
  }

  private applyRowsFromResponse(data: any): void {
    const rows = Array.isArray(data) ? data : [];
    this.packageMappings = rows.map(row => mapPackageMappingFromApi(row));
  }

  onDiscard(): void {
    if (this.isSaving) {
      return;
    }
    for (const row of this.packageMappings) {
      row.way_package_id = row.saved_way_package_id;
      row.fieldError = '';
    }
    this.listError = '';
  }

  onSave(): void {
    if (!this.companyId || !this.locationId || this.isSaving || !this.isDirty) {
      return;
    }

    const validation = validatePackageMappings(this.packageMappings);
    this.listError = validation.error;
    if (!validation.valid) {
      return;
    }

    const companyId = this.companyId;
    const locationId = this.locationId;
    const requestId = ++this.saveRequestId;
    this.isSaving = true;
    this.listError = '';

    const payload = {
      mappings: this.packageMappings.map(row => ({
        package_id: row.package_id,
        way_package_id: row.way_package_id.trim() || null,
      })),
    };

    this.packageMappingService.updatePackageMappings(companyId, locationId, payload).subscribe({
      next: response => {
        if (
          requestId !== this.saveRequestId ||
          companyId !== this.companyId ||
          locationId !== this.locationId
        ) {
          return;
        }
        if (response.status) {
          // Keep inputs locked until the filtered reload finishes.
          this.loadPackageMappings({ silent: true, afterSaveRequestId: requestId });
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Package mappings saved',
          });
        } else {
          this.isSaving = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to save package mappings',
          });
        }
      },
      error: err => {
        if (
          requestId !== this.saveRequestId ||
          companyId !== this.companyId ||
          locationId !== this.locationId
        ) {
          return;
        }
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to save package mappings',
        });
      },
    });
  }

  private loadPackageMappings(
    options: { silent?: boolean; afterSaveRequestId?: number } = {}
  ): void {
    if (!this.companyId || !this.locationId) {
      this.saveRequestId += 1;
      this.packageMappings = [];
      this.loading = false;
      this.isSaving = false;
      this.loadFailed = false;
      this.listError = '';
      return;
    }

    const companyId = this.companyId;
    const locationId = this.locationId;

    if (!options.silent) {
      // Invalidate any in-flight save for the previous context.
      this.saveRequestId += 1;
      this.isSaving = false;
      this.loading = true;
      this.packageMappings = [];
    }

    this.loadFailed = false;
    this.listError = '';

    this.packageMappingService.listPackageMappings(companyId, locationId).subscribe({
      next: response => {
        if (companyId !== this.companyId || locationId !== this.locationId) {
          return;
        }
        this.loading = false;
        if (response.status) {
          this.applyRowsFromResponse(response.data ?? []);
        } else {
          this.packageMappings = [];
          this.loadFailed = true;
          this.listError = response.message || 'Failed to load package mappings';
        }
        if (
          options.afterSaveRequestId !== undefined &&
          options.afterSaveRequestId === this.saveRequestId
        ) {
          this.isSaving = false;
        }
      },
      error: err => {
        if (companyId !== this.companyId || locationId !== this.locationId) {
          return;
        }
        this.loading = false;
        this.packageMappings = [];
        this.loadFailed = true;
        this.listError = err?.error?.message || 'Failed to load package mappings';
        if (
          options.afterSaveRequestId !== undefined &&
          options.afterSaveRequestId === this.saveRequestId
        ) {
          this.isSaving = false;
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.listError,
        });
      },
    });
  }
}
